// ** React Imports
import { useState, Fragment } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { pathWithPreservedSearch } from "src/@core/utils/urlQueryPreservation";

// ** MUI Imports
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

// ** Icons Imports
import LogoutVariant from "mdi-material-ui/LogoutVariant";
import AccountOutline from "mdi-material-ui/AccountOutline";
import ReceiptTextOutline from "mdi-material-ui/ReceiptTextOutline";

// ** Context
import { useAuth } from "src/hooks/useAuth";
import getAvatarPicture from "src/@core/components/profile";

/******************************************************************
 * ##: UserDropdown
 * AppBar account menu (style-v3): a rounded-square avatar trigger and
 * a rounded dropdown showing name + e-mail, a link to the profile and
 * a sign-out action. Colours come from the MUI theme so it follows
 * light / dark automatically.
 * @param {Object} props - settings (direction)
 * @returns {JSX.Element} - the avatar + account menu
 * History:
 * 05-07-2026: Restyled (bigger avatar, name+email header, icon tiles)
 ******************************************************************/
const UserDropdown = (props) => {
  // ** Props
  const { settings } = props;

  // ** States
  const [anchorEl, setAnchorEl] = useState(null);

  // ** Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const auth = useAuth();

  // ** Vars
  const { direction } = settings;
  const user = auth?.user;

  const handleDropdownOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = (url) => {
    if (url) {
      navigate(url);
    }
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleDropdownClose();
  };

  // Shared menu-item styling (rounded row with accent hover)
  const menuItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: 2.5,
    mx: 1.5,
    my: 0.25,
    px: 1.5,
    py: 1.25,
    borderRadius: "10px",
    fontSize: "0.9rem",
    fontWeight: 500,
    color: "text.primary",
    "&:hover": { backgroundColor: (theme) => `${theme.palette.primary.main}0F` },
  };

  // Icon tile inside a menu row
  const tileStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 34,
    borderRadius: "9px",
    color: "primary.main",
    backgroundColor: (theme) => `${theme.palette.primary.main}16`,
    "& svg": { fontSize: "1.2rem" },
  };

  return (
    <Fragment>
      <Avatar
        alt={user?.name}
        src={getAvatarPicture(user?.name)}
        variant="rounded"
        onClick={handleDropdownOpen}
        sx={{
          cursor: "pointer",
          width: 38,
          height: 38,
          borderRadius: "11px",
          border: (theme) => `2px solid ${theme.palette.primary.main}33`,
          transition: "border-color .2s",
          "&:hover": { borderColor: "primary.main" },
        }}
      />
      <Menu
        className="user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleDropdownClose()}
        PaperProps={{
          sx: {
            width: 290,
            mt: 2.5,
            overflow: "visible",
            borderRadius: "16px",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: "0 16px 44px -14px rgba(44, 46, 68, 0.32)",
            zIndex: 10000,
            "& .MuiList-root": { p: 0 },
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: direction === "ltr" ? "right" : "left" }}
        transformOrigin={{ vertical: "top", horizontal: direction === "ltr" ? "right" : "left" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            p: 3,
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}14, transparent 70%)`,
          }}
        >
          <Avatar
            alt={user?.name}
            src={getAvatarPicture(user?.name)}
            variant="rounded"
            sx={{ width: 46, height: 46, borderRadius: "13px" }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 600, fontSize: "0.95rem", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {user?.name}
            </Typography>
            <Typography
              sx={{ fontSize: "0.8rem", color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {user?.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ m: 0 }} />

        <Box sx={{ py: 1.5 }}>
          <MenuItem
            sx={menuItemStyle}
            onClick={() => handleDropdownClose(pathWithPreservedSearch("/profile", location.search))}
          >
            <Box sx={tileStyle}>
              <AccountOutline />
            </Box>
            O meu perfil
          </MenuItem>

          <MenuItem
            sx={menuItemStyle}
            onClick={() => handleDropdownClose(pathWithPreservedSearch("/my-purchases", location.search))}
          >
            <Box sx={tileStyle}>
              <ReceiptTextOutline />
            </Box>
            As minhas compras
          </MenuItem>

          <MenuItem
            sx={{ ...menuItemStyle, color: "error.main", "&:hover": { backgroundColor: (theme) => `${theme.palette.error.main}0F` } }}
            onClick={handleLogout}
          >
            <Box sx={{ ...tileStyle, color: "error.main", backgroundColor: (theme) => `${theme.palette.error.main}16` }}>
              <LogoutVariant />
            </Box>
            Terminar sessão
          </MenuItem>
        </Box>
      </Menu>
    </Fragment>
  );
};

export default UserDropdown;
